// Pure logic for the custom tags panel. Deterministic so it can be unit tested without
// mounting the panel or touching Apollo.

import { TAG_TYPES } from './constants';

/**
 * Split the client's tag list into the ones it owns and the ones it inherits from its
 * organization. Org tags are read-only here — they are managed in the CorporateTags page.
 * @param {Array} tags every tag on the client, owned and inherited
 * @param {string} user_id the account's own client id
 * @returns {{ owned: Array, org: Array }}
 */
export const splitTags = (tags, user_id) => ({
    owned: (tags || []).filter((tag) => tag.client_id === user_id),
    org: (tags || []).filter((tag) => !tag.client_id && tag.organization_id),
});

export const sortByName = (tags) => [...(tags || [])].sort((a, b) => (a.tag || '').localeCompare(b.tag || ''));

/**
 * Overlay the unsaved edits on the saved tags, preserving sort order. A tag with no pending
 * edit passes through untouched.
 */
export const mergeUpdates = (sortedTags, updates) => {
    const byId = { ...Object.fromEntries((sortedTags || []).map((t) => [t.tag_id, t])), ...(updates || {}) };
    return (sortedTags || []).map((tag) => byId[tag.tag_id] || tag);
};

/** The TAG_TYPES keys a tag has switched on, as Autocomplete option objects. */
export const selectedTypes = (tag) =>
    Object.keys(TAG_TYPES).reduce(
        (acc, type) => (tag?.[type] ? [...acc, { value: type, label: TAG_TYPES[type] }] : acc),
        []
    );

export const TYPE_OPTIONS = Object.entries(TAG_TYPES).map(([value, label]) => ({ value, label }));

/** Human-readable type list for a read-only tag, e.g. "Route Tag, Item Tag". */
export const typeLabels = (tag) =>
    selectedTypes(tag)
        .map((option) => option.label)
        .join(', ');

/**
 * A tag patched so exactly the chosen types are on and the rest are off. Every key in
 * TAG_TYPES is written, so clearing a type persists as `false` rather than going missing.
 */
export const withTypes = (tag, options) => {
    const chosen = Object.fromEntries((options || []).map((option) => [option.value, true]));
    return {
        ...tag,
        ...Object.fromEntries(Object.keys(TAG_TYPES).map((type) => [type, !!chosen[type]])),
    };
};

export const isAlwaysApplied = (tag, user_id) => (tag?.always_apply_clients || []).some((c) => c.client_id === user_id);

/** Add or remove this account from a tag's always-apply list without disturbing other clients. */
export const withAlwaysApply = (tag, user_id, on) => {
    const current = tag?.always_apply_clients || [];
    if (on) {
        return isAlwaysApplied(tag, user_id)
            ? tag
            : { ...tag, always_apply_clients: [...current, { client_id: user_id }] };
    }
    return { ...tag, always_apply_clients: current.filter((c) => c.client_id !== user_id) };
};

/** Tags shaped for UPSERT_TAGS — the relationship fields are not columns and must come off. */
export const stripForUpsert = (tags) =>
    (tags || []).map(({ __typename, client, always_apply_clients, ...rest }) => rest);

/**
 * Flattened tag_always_apply_clients rows for the edited tags. REPLACE_ALWAYS_APPLY_CLIENTS
 * deletes by tag_id then re-inserts these, so a tag switched off contributes no rows and ends
 * up with none — which is the delete.
 */
export const buildAlwaysApplyRows = (dirtyIds, tags) =>
    (dirtyIds || []).reduce((acc, id) => {
        const tag = (tags || []).find((t) => t.tag_id === id);
        const rows = (tag?.always_apply_clients || []).map((c) => ({ tag_id: id, client_id: c.client_id }));
        return [...acc, ...rows];
    }, []);
